"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterLabel } from "@/app/(saas)/(app)/businesses/_components/filters/filter-label";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

function getMarketCountryCodeFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/businesses\/([a-z]{2})(?:\/|$)/);
  return match?.[1]?.toUpperCase();
}

export function FinancingFilter() {
  const t = useTranslations();
  const pathname = usePathname();
  const marketCountryCode = getMarketCountryCodeFromPath(pathname);
  const showUsOnlyFinancingFilters = marketCountryCode !== "NZ";
  const [filters, setFilters] = useAtom(businessFiltersAtom);

  function handleFinancingAvailableChange(value: boolean) {
    setFilters((prev) => ({
      ...prev,
      financingAvailable: value,
    }));
  }

  function handleSBAPrequalifiedChange(value: boolean) {
    setFilters((prev) => ({
      ...prev,
      sbaPrequalified: value,
    }));
  }

  function handleVisaQualifiedChange(value: boolean) {
    setFilters((prev) => ({
      ...prev,
      visaQualified: value,
    }));
  }

  function resetFinancing() {
    setFilters((prev) => ({
      ...prev,
      financingAvailable: null,
      sbaPrequalified: null,
      visaQualified: null,
    }));
  }

  const facets = useListingFacetCounts();

  function renderCount(value: number | undefined) {
    if (facets.isLoading) {
      return (
        <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
      );
    }
    return value ?? 0;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Checkbox
            id="financingAvailable"
            checked={filters.financingAvailable ?? false}
            onCheckedChange={handleFinancingAvailableChange}
            className="w-5 h-5 border-2 rounded-[6px]"
          />

          <FilterLabel
            htmlFor="financingAvailable"
            className="flex-1"
            hint={t(
              "saas.search.filters.fields.financing.financingAvailable.hint",
            )}
          >
            {t("saas.search.filters.fields.financing.financingAvailable.label")}
          </FilterLabel>

          <span className="text-xs text-muted-foreground font-data tabular-nums">
            {renderCount(facets.data?.financingCounts.buyerFinancingAvailable)}
          </span>
        </div>

        {showUsOnlyFinancingFilters && (
          <>
            <div className="flex items-center justify-between gap-3">
              <Checkbox
                id="sbaPrequalified"
                checked={filters.sbaPrequalified ?? false}
                onCheckedChange={handleSBAPrequalifiedChange}
                className="w-5 h-5 border-2 rounded-[6px]"
              />

              <FilterLabel
                htmlFor="sbaPrequalified"
                className="flex-1"
                hint={t(
                  "saas.search.filters.fields.financing.sbaPrequalified.hint",
                )}
              >
                {t(
                  "saas.search.filters.fields.financing.sbaPrequalified.label",
                )}
              </FilterLabel>

              <span className="text-xs text-muted-foreground font-data tabular-nums">
                {renderCount(facets.data?.financingCounts.sbaPrequalified)}
              </span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <Checkbox
                id="visaQualified"
                checked={filters.visaQualified ?? false}
                onCheckedChange={handleVisaQualifiedChange}
                className="w-5 h-5 border-2 rounded-[6px]"
              />

              <FilterLabel
                htmlFor="visaQualified"
                className="flex-1"
                hint={t(
                  "saas.search.filters.fields.financing.visaQualified.hint",
                )}
              >
                {t("saas.search.filters.fields.financing.visaQualified.label")}
              </FilterLabel>

              <span className="text-xs text-muted-foreground font-data tabular-nums">
                {renderCount(facets.data?.financingCounts.visaQualified)}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between border-t pt-3">
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          onClick={resetFinancing}
        >
          Reset
        </button>

        <span className="text-xs text-muted-foreground">
          {facets.isLoading ? (
            <span className="inline-block w-8 h-3 bg-muted animate-pulse rounded" />
          ) : (
            <span className="font-data tabular-nums text-foreground">
              {facets.data?.total ?? 0}
            </span>
          )}{" "}
          businesses match
        </span>
      </div>
    </div>
  );
}
